import axios, { AxiosError, AxiosResponse } from "axios";
import { useSnackbar } from "notistack";
import { useEffect } from "react";
import { useIntl } from "react-intl";
import {
  InfiniteData,
  useInfiniteQuery,
  UseInfiniteQueryOptions,
  UseInfiniteQueryResult,
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query";

import { useDispatch } from "react-redux";

import { ActivateFormSubmission } from "components/authentication/ActivateForm";
import { User, UserData } from "components/users/User";
import { logoutSuccess } from "containers/Shared/App/actions";
import { Api } from "lib/Api";
import { Pageable } from "types/common/Pageable";
import { PageQuery } from "types/common/PageQuery";
import { PointsBreakdown } from "types/gamification/PointsBreakdown";
import { GroupMinimal } from "types/group/GroupMinimal";
import { AdminUserPayload } from "types/users/AdminUserPayload";
import { PasswordResetSchema } from "types/password/PasswordResetSchema";
import AuthService from "utils/authService";
import { getGetOnboardingStatusQueryKey } from "generated/api/profile-onboarding-profile-settings/profile-onboarding-profile-settings";

export const CURRENT_USER_KEY = "currentUser";

const CACHE_TIME = 5 * 60 * 1000;

export const api = new Api<User, PageQuery & Filters>("users", "user");

export const USERS_KEY = "users";

type Query = Omit<Partial<PageQuery>, "page">;

interface Filters {
  minimal?: boolean;
  query_scopes?: string;
}

type Users = Pageable<User>;

type UsersInfiniteData = InfiniteData<Users, number>;
type UsersInfiniteOptions = Omit<
  UseInfiniteQueryOptions<
    Users,
    AxiosError,
    UsersInfiniteData,
    readonly unknown[],
    number
  >,
  | "queryKey"
  | "queryFn"
  | "getNextPageParam"
  | "getPreviousPageParam"
  | "initialPageParam"
>;

export type UserUsersQuery = Query & Filters;

export function useUsers(
  query?: UserUsersQuery,
  options?: UsersInfiniteOptions,
): UseInfiniteQueryResult<UsersInfiniteData, AxiosError> {
  return useInfiniteQuery({
    queryKey: [USERS_KEY, query],
    initialPageParam: 0,
    queryFn: ({ pageParam }) =>
      api
        .findAll({
          count: 10,
          minimal: true,
          ...query,
          page: pageParam,
        })
        .then((resp) => resp.data),
    staleTime: CACHE_TIME,
    gcTime: CACHE_TIME,
    ...(options ?? {}),
    // Non-overridable options
    getNextPageParam: (lastPage) =>
      lastPage.page.last ? undefined : lastPage.page.number + 1,
    getPreviousPageParam: (firstPage) =>
      firstPage.page.first ? undefined : firstPage.page.number - 1,
  });
}

export function useUser(
  id: number,
  options?: Omit<UseQueryOptions<User, AxiosError>, "queryKey" | "queryFn">,
): UseQueryResult<User, AxiosError> {
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();

  const result = useQuery({
    queryKey: [USERS_KEY, id],
    queryFn: () => api.findById(id).then((resp) => resp.data),
    ...options,
  });

  useEffect(() => {
    if (!result.isError) {
      return;
    }
    enqueueSnackbar(
      intl.formatMessage({
        defaultMessage: "Failed to load user",

        description:
          "Snackbar error message displayed when a user data fail to load",
      }),
      { variant: "warning" },
    );
  }, [enqueueSnackbar, intl, result.isError]);

  return result;
}

export function useUserMemberships(
  id: number,
): UseQueryResult<GroupMinimal[], AxiosError> {
  return useQuery({
    queryKey: [USERS_KEY, "UserMemberships", id],
    queryFn: () =>
      axios
        .get(`/api/v1/users/${id}/memberships`)
        .then((response) => response.data as GroupMinimal[]),
  });
}

export function useHasCurrentUserCompletedAchievementsTour(): UseQueryResult<
  boolean,
  AxiosError
> {
  return useQuery({
    queryKey: ["AchievementsTourCompleted"],
    queryFn: () =>
      axios
        .get("/api/v1/user/achievements_tour/completed")
        .then((response) => response.data as boolean),
  });
}

export function useUserPoints(): UseQueryResult<PointsBreakdown, AxiosError> {
  return useQuery({
    queryKey: ["UserPoints"],
    queryFn: () =>
      axios
        .get("/api/v1/user/user_points_breakdown")
        .then((resp) => resp.data as PointsBreakdown),
  });
}

export function useTokens(
  options?: UseQueryOptions<number, AxiosError>,
): UseQueryResult<number, AxiosError> {
  return useQuery({
    queryKey: ["UserTokens"],
    queryFn: () =>
      axios
        .get("/api/v1/user/tokens")
        .then((response) => response.data as number),
    ...options,
  });
}

export function useRestartAchievementsTourMutation(): () => Promise<
  AxiosResponse<never>
> {
  const queryClient = useQueryClient();

  return () =>
    axios
      .post<never>("/api/v1/user/achievements_tour/incomplete")
      .then(async (resp) => {
        await queryClient.invalidateQueries({
          queryKey: ["AchievementsTourCompleted"],
        });
        return resp;
      });
}

export function useAlternativeActivationMutation(
  options?: UseMutationOptions<
    ActivateFormSubmission,
    AxiosError<{ message?: string }>,
    ActivateFormSubmission
  >,
): UseMutationResult<
  ActivateFormSubmission,
  AxiosError,
  ActivateFormSubmission
> {
  const { enqueueSnackbar } = useSnackbar();
  const intl = useIntl();

  return useMutation({
    mutationFn: async (data: ActivateFormSubmission) => {
      const promise = axios.post("/api/v1/users/activate", { user: data });
      const resp = await promise;
      return resp.data as ActivateFormSubmission;
    },

    ...options,

    onError: (...args) => {
      const [error] = args;
      const errorMessage =
        error.response?.data.message ??
        intl.formatMessage({
          defaultMessage: "An error occurred when trying to activate",

          description:
            "Snackbar error message displayed when the user alternative activation submission errors for an unexpected reason",
        });
      enqueueSnackbar(errorMessage, { variant: "warning" });

      return options?.onError?.(...args);
    },
  });
}

export function useCurrentUser(
  options?: Omit<UseQueryOptions<UserData, AxiosError>, "queryKey" | "queryFn">,
): UseQueryResult<UserData, AxiosError> {
  return useQuery({
    queryKey: [CURRENT_USER_KEY],
    queryFn: getCurrentUser,
    ...options,
  });
}

export async function getCurrentUser() {
  const resp = await axios.get<UserData>("/api/v1/user/user_data");
  const userData: UserData = resp.data;
  return userData;
}

interface AchievementAssignmentMutationData {
  userId: number;
  achievementId: number;
  type: "assign" | "revoke";
}

export function useAchievementAssignmentMutation(
  options?: UseMutationOptions<
    AchievementAssignmentMutationData,
    AxiosError,
    AchievementAssignmentMutationData
  >,
): UseMutationResult<
  AchievementAssignmentMutationData,
  AxiosError,
  AchievementAssignmentMutationData
> {
  const { enqueueSnackbar } = useSnackbar();
  const intl = useIntl();

  return useMutation({
    mutationFn: async (data: AchievementAssignmentMutationData) => {
      const resp =
        data.type === "assign"
          ? await axios.post(
              `/api/v1/users/${data.userId}/achievements/${data.achievementId}`,
            )
          : await axios.delete(
              `/api/v1/users/${data.userId}/achievements/${data.achievementId}`,
            );

      return resp.data as AchievementAssignmentMutationData;
    },

    ...options,

    onError: (...args) => {
      const [_error, vars] = args;
      enqueueSnackbar(
        vars.type === "assign"
          ? intl.formatMessage({
              defaultMessage:
                "An error occurred while assigning the selected achievement",

              description:
                "Snackbar message displayed when the current user assigning an achievement to another user is not successful",
            })
          : intl.formatMessage({
              defaultMessage:
                "An error occurred while revoking the selected achievement",

              description:
                "Snackbar message displayed when the current user un-assigning an achievement from another user is not successful",
            }),
        { variant: "warning" },
      );

      return options?.onError?.(...args);
    },
  });
}

export function useUnsubscribeUser(
  options?: UseMutationOptions<void, AxiosError, string>,
): UseMutationResult<void, AxiosError, string> {
  return useMutation({
    mutationFn: (data: string) =>
      axios
        .post("/api/v1/users/unsubscribe", { email: data })
        .then((resp) => resp.data as undefined),

    ...options,
  });
}

export function useResubscribeUser(
  options?: UseMutationOptions<void, AxiosError, string>,
): UseMutationResult<void, AxiosError, string> {
  return useMutation({
    mutationFn: (data: string) =>
      axios
        .post("/api/v1/users/resubscribe", { email: data })
        .then((resp) => resp.data as undefined),

    ...options,
  });
}

/**
 * Reset onboarding for a user so that the user is marked as if never has seen the onboarding page.
 */
export function useResetOnboardingMutation(
  options?: UseMutationOptions<void, AxiosError, number>,
): UseMutationResult<void, AxiosError, number> {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const intl = useIntl();

  return useMutation({
    mutationFn: (userId: number) =>
      axios
        .delete(`/api/v1/users/seen_onboarding/${userId}`)
        .then((resp) => resp.data as undefined),

    ...options,

    onSuccess: async (...args) => {
      enqueueSnackbar(
        intl.formatMessage({
          defaultMessage: "Onboarding reset successfully",
          description:
            "Snackbar message displayed when the onboarding reset succeeds",
        }),
        { variant: "success" },
      );
      await queryClient.invalidateQueries({
        queryKey: getGetOnboardingStatusQueryKey(),
      });

      return options?.onSuccess?.(...args);
    },

    onError: (...args) => {
      enqueueSnackbar(
        intl.formatMessage({
          defaultMessage: "An error occurred while trying to reset onboarding",
          description:
            "Snackbar error message displayed when the onboarding reset fails",
        }),
        { variant: "warning" },
      );

      return options?.onError?.(...args);
    },
  });
}

export function useResendOnboardingInvitation(
  options?: UseMutationOptions<void, AxiosError, number>,
): UseMutationResult<void, AxiosError, number> {
  return useMutation({
    mutationFn: (userId: number) =>
      axios
        .post(`/api/v1/users/resend_invitation/`, { id: userId })
        .then((resp) => resp.data as undefined),
    ...options,
  });
}

interface ResetPasswordTokenResponse {
  user: User;
  token: string;
}

export function useUserByPasswordResetToken(
  token: string,
  options?: UseQueryOptions<ResetPasswordTokenResponse, AxiosError>,
): UseQueryResult<ResetPasswordTokenResponse, AxiosError> {
  return useQuery({
    queryKey: [USERS_KEY, "password_reset_token", token],
    queryFn: () =>
      axios
        .post(`/api/v1/users/reset_password_token`, { token })
        .then((response) => response.data as ResetPasswordTokenResponse),
    ...options,
  });
}

interface ResetPasswordPayload {
  token: string;
  user: PasswordResetSchema;
}

export function useResetPassword(
  options?: UseMutationOptions<void, AxiosError, ResetPasswordPayload>,
): UseMutationResult<void, AxiosError, ResetPasswordPayload> {
  return useMutation({
    mutationFn: (data: ResetPasswordPayload) =>
      axios
        .post("/api/v1/users/password_reset", data)
        .then((resp) => resp.data as undefined),

    ...options,
  });
}

/**
 * Logs out the current user from API/V1, then removes the JWT token from local storage and reset
 * the auth state in the Redux store.
 */
export function useLegacyLogout(
  options?: UseMutationOptions<void, AxiosError>,
): UseMutationResult<void, AxiosError, void> {
  const dispatch = useDispatch();
  const logoutSuccessAction = logoutSuccess;
  return useMutation({
    mutationFn: () =>
      axios.delete("/api/v1/sessions/logout").then(() => undefined),
    ...options,
    onSuccess(...args) {
      const [_data, _vars, _context] = args;
      AuthService.discardJwt();
      dispatch(logoutSuccessAction());

      return options?.onSuccess?.(...args);
    },
  });
}

interface UpdateUserPayload {
  id: number | string;
  user: AdminUserPayload;
}

export function useUpdateUser(options?: {
  mutation?: UseMutationOptions<User, AxiosError, UpdateUserPayload>;
}): UseMutationResult<User, AxiosError, UpdateUserPayload> {
  return useMutation({
    mutationFn: ({ id, user }: UpdateUserPayload) =>
      axios
        .put(`/api/v1/users/${id}`, { user })
        .then((resp) => resp.data as User),
    ...options?.mutation,
  });
}

export async function legacyLogout() {
  return axios.delete("/api/v1/sessions/logout");
}
